import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { systemService } from '../../services/api';

export interface DetectionThreshold {
    name: string;
    value: number | string | boolean;
    default: number | string | boolean;
    unit: string;
    what: string;
    used_in: string;
    is_default: boolean;
}

export interface DetectionThresholdGroup {
    key: string;
    label: string;
    thresholds: DetectionThreshold[];
}

export interface DetectionThresholdsData {
    read_only: boolean;
    note: string;
    groups: DetectionThresholdGroup[];
    screening_priority_order?: string[];
}

const fmt = (v: unknown): string => (typeof v === 'number' ? v.toLocaleString('en-IN') : String(v));

/** Read-only view of the thresholds the engine is running with (SET01). */
export const DetectionThresholdsView: React.FC<{ data: DetectionThresholdsData }> = ({ data }) => (
    <div className="space-y-6" data-testid="detection-thresholds-tab">
        <p className="text-sm text-muted-foreground" data-testid="detection-thresholds-note">
            These values are fixed by the platform and cannot be changed here. Changes are made on request.
        </p>
        {data.groups.map(group => (
            <section key={group.key} data-testid={`threshold-group-${group.key}`}>
                <h2 className="text-lg font-semibold text-foreground mb-2">{group.label}</h2>
                <div className="overflow-x-auto border border-border rounded-lg">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="px-3 py-2">Name</th>
                                <th className="px-3 py-2">Value</th>
                                <th className="px-3 py-2">Default</th>
                                <th className="px-3 py-2">Unit</th>
                                <th className="px-3 py-2">What it does</th>
                            </tr>
                        </thead>
                        <tbody>
                            {group.thresholds.map(t => (
                                <tr key={t.name} className="border-t border-border align-top" data-testid={`threshold-row-${t.name}`}>
                                    <td className="px-3 py-2 font-mono text-xs">{t.name}</td>
                                    <td
                                        className={`px-3 py-2 font-medium ${t.is_default ? 'text-foreground' : 'text-amber-600 bg-amber-500/10'}`}
                                        data-testid={`threshold-value-${t.name}`}
                                        data-differs={t.is_default ? 'false' : 'true'}
                                    >
                                        {fmt(t.value)}
                                        {!t.is_default && <span className="ml-1 text-xs font-normal" data-testid={`threshold-differs-${t.name}`}>(differs from default)</span>}
                                    </td>
                                    <td className="px-3 py-2 text-muted-foreground">{fmt(t.default)}</td>
                                    <td className="px-3 py-2 text-muted-foreground">{t.unit}</td>
                                    <td className="px-3 py-2">
                                        {t.what}
                                        <div className="text-xs text-muted-foreground mt-0.5">Used in: {t.used_in}</div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        ))}
        {data.screening_priority_order && data.screening_priority_order.length > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="screening-priority-order">
                Screening priority order when a cap or budget cuts a sweep short: {data.screening_priority_order.join(', ')}.
            </p>
        )}
    </div>
);

export const DetectionThresholdsTab: React.FC = () => {
    const [data, setData] = useState<DetectionThresholdsData | null>(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        systemService.getDetectionThresholds()
            .then((d: DetectionThresholdsData) => { if (!cancelled) setData(d); })
            .catch(() => { if (!cancelled) setError(true); });
        return () => { cancelled = true; };
    }, []);

    if (error) return <p className="text-sm text-muted-foreground">The thresholds could not be loaded.</p>;
    if (!data) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading</div>;
    return <DetectionThresholdsView data={data} />;
};

export default DetectionThresholdsTab;
